import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, Loader2Icon, CalendarIcon, PlusIcon, AlertCircleIcon, UsersIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CreateAcademicYearModal } from '../components/CreateAcademicYearModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
export function OrganizationSettings() {
  const userRole = 'admin';
  // State
  const [activeTab, setActiveTab] = useState<'general'>('general');
  const [isCreateYearModalOpen, setIsCreateYearModalOpen] = useState(false);
  const [yearToActivate, setYearToActivate] = useState<number | null>(null);
  // Mock Academic Years
  const [academicYears, setAcademicYears] = useState([{
    id: '1',
    name: '2024-2025',
    start: '2024-08-01',
    end: '2025-06-30',
    active: true
  }, {
    id: '2',
    name: '2023-2024',
    start: '2023-08-01',
    end: '2024-06-30',
    active: false
  }, {
    id: '3',
    name: '2025-2026',
    start: '2025-08-01',
    end: '2026-06-30',
    active: false
  }]);
  // Helper to check if a year is in the past
  const isPastYear = (endDate: string) => {
    return new Date(endDate) < new Date();
  };
  // Helper to format date as MMM DD, YYYY
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };
  // Sort academic years: Active/Upcoming first (sorted by start date desc), then Past (sorted by start date desc)
  const sortedAcademicYears = useMemo(() => {
    return [...academicYears].sort((a, b) => {
      const aIsPast = isPastYear(a.end);
      const bIsPast = isPastYear(b.end);
      // If one is past and other isn't, non-past comes first
      if (aIsPast !== bIsPast) {
        return aIsPast ? 1 : -1;
      }
      // If active status differs, active comes first
      if (a.active !== b.active) {
        return a.active ? -1 : 1;
      }
      // Otherwise sort by start date descending (newest first)
      return new Date(b.start).getTime() - new Date(a.start).getTime();
    });
  }, [academicYears]);
  const handleCreateYear = async (data: any) => {
    console.log('Creating academic year:', data);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    const newYear = {
      id: String(academicYears.length + 1),
      name: data.name,
      start: data.startDate,
      end: data.endDate,
      active: data.setAsActive || false
    };
    // If setting as active, deactivate others
    if (data.setAsActive) {
      setAcademicYears(prev => [...prev.map(y => ({
        ...y,
        active: false
      })), newYear]);
    } else {
      setAcademicYears(prev => [...prev, newYear]);
    }
    setIsCreateYearModalOpen(false);
  };
  const handleSetActive = async () => {
    if (!yearToActivate) return;
    console.log('Setting year as active:', yearToActivate);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    setAcademicYears(prev => prev.map(year => ({
      ...year,
      active: year.id === yearToActivate
    })));
    setYearToActivate(null);
  };
  const getYearName = (id: string) => {
    return academicYears.find(y => y.id === id)?.name || '';
  };
  return <Tooltip.Provider delayDuration={200} data-id="element-3512">
      <div className="flex flex-col min-h-screen bg-background" data-id="element-3513">
        <Header data-id="element-3514" />
        <div className="flex flex-1 overflow-hidden" data-id="element-3515">
          <div className="hidden md:block" data-id="element-3516">
            <Sidebar data-id="element-3517" />
          </div>
          <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-3518">
            <main className="flex-1" data-id="element-3519">
              <div className="max-w-4xl mx-auto px-6 py-8" data-id="element-3520">
                {/* Header */}
                <div className="mb-8" data-id="element-3521">
                  <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-3522">
                    Organization Settings
                  </h1>
                  <p className="text-text-secondary" data-id="element-3523">
                    Manage academic years, policies, and organization
                    configuration
                  </p>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-6 mb-8 border-b border-custom" data-id="element-3524">
                  <button onClick={() => setActiveTab('general')} className={`px-4 py-3 text-sm font-semibold transition-all relative ${activeTab === 'general' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-3525">
                    General
                    {activeTab === 'general' && <motion.div layoutId="activeSettingsTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-3526" />}
                  </button>
                </div>

                {/* General Tab Content */}
                {activeTab === 'general' && <div data-id="element-3527">
                    {/* Academic Years Section */}
                    <div className="bg-surface-elevated rounded-2xl border border-custom shadow-sm overflow-hidden" data-id="element-3528">
                      <div className="p-8" data-id="element-3529">
                        <div className="flex items-start justify-between mb-6" data-id="element-3530">
                          <div className="flex items-start gap-4" data-id="element-3531">
                            <div className="w-12 h-12 rounded-xl bg-[#2563EB] bg-opacity-10 flex items-center justify-center flex-shrink-0" data-id="element-3532">
                              <CalendarIcon size={24} className="text-[#2563EB]" data-id="element-3533" />
                            </div>
                            <div data-id="element-3534">
                              <h2 className="text-xl font-bold text-text-primary mb-2" data-id="element-3535">
                                Academic Years
                              </h2>
                              <p className="text-sm text-text-secondary leading-relaxed" data-id="element-3536">
                                Manage school years. Only current or future
                                years can be set as active.
                              </p>
                            </div>
                          </div>
                          <button onClick={() => setIsCreateYearModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-custom rounded-xl text-sm font-semibold text-text-primary hover:bg-gray-50 transition-colors" data-id="element-3537">
                            <PlusIcon size={16} data-id="element-3538" />
                            Add Year
                          </button>
                        </div>

                        <div className="border border-custom rounded-xl overflow-hidden" data-id="element-3539">
                          <table className="w-full" data-id="element-3540">
                            <thead className="bg-gray-50 border-b border-custom" data-id="element-3541">
                              <tr data-id="element-3542">
                                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3543">
                                  Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3544">
                                  Duration
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3545">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-3546">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white" data-id="element-3547">
                              {sortedAcademicYears.map(year => {
                            const isPast = isPastYear(year.end);
                            return <tr key={year.id} className="hover:bg-gray-50 transition-colors" data-id="element-3548">
                                    <td className="px-6 py-4 font-medium text-text-primary" data-id="element-3549">
                                      {year.name}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-text-secondary font-mono" data-id="element-3550">
                                      {formatDate(year.start)} —{' '}
                                      {formatDate(year.end)}
                                    </td>
                                    <td className="px-6 py-4" data-id="element-3551">
                                      {year.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#00B75F]/10 text-[#00B75F]" data-id="element-3552">
                                          ACTIVE
                                        </span> : isPast ? <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" data-id="element-3553">
                                          PAST
                                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600" data-id="element-3554">
                                          UPCOMING
                                        </span>}
                                    </td>
                                    <td className="px-6 py-4 text-right" data-id="element-3555">
                                      {!year.active && !isPast && <button onClick={() => setYearToActivate(year.id)} className="text-sm font-semibold text-[#2563EB] hover:underline" data-id="element-3556">
                                          Set Active
                                        </button>}
                                      {!year.active && isPast && <Tooltip.Root data-id="element-3557">
                                          <Tooltip.Trigger asChild data-id="element-3558">
                                            <span className="inline-flex items-center gap-1 text-xs text-text-tertiary cursor-not-allowed" data-id="element-3559">
                                              Cannot activate
                                              <AlertCircleIcon size={12} data-id="element-3560" />
                                            </span>
                                          </Tooltip.Trigger>
                                          <Tooltip.Portal data-id="element-3561">
                                            <Tooltip.Content className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg z-50" sideOffset={5} data-id="element-3562">
                                              Past academic years cannot be set
                                              as active
                                              <Tooltip.Arrow className="fill-gray-900" data-id="element-3563" />
                                            </Tooltip.Content>
                                          </Tooltip.Portal>
                                        </Tooltip.Root>}
                                    </td>
                                  </tr>;
                          })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>
            </main>
            <Footer data-id="element-3564" />
          </div>
        </div>
      </div>

      <CreateAcademicYearModal isOpen={isCreateYearModalOpen} onClose={() => setIsCreateYearModalOpen(false)} onCreate={handleCreateYear} hasActiveYear={academicYears.some(y => y.active)} data-id="element-3565" />

      <ConfirmationModal isOpen={!!yearToActivate} onClose={() => setYearToActivate(null)} onConfirm={handleSetActive} title="Change Active Academic Year?" description={`Are you sure you want to set ${yearToActivate ? getYearName(yearToActivate) : ''} as the active year? This will archive the current active year. You cannot reactivate past years once they end.`} confirmLabel="Yes, Set Active" type="warning" data-id="element-3566" />
    </Tooltip.Provider>;
}